/**
 * JourneyAnimation — character-driven storytelling loop (option D).
 * One entrepreneur "Sam" walks through 5 scenes with speech bubbles.
 * Premium warm palette. Reduced-motion + off-screen pause + fallback supported.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const SCENE_DURATION_MS = 2400; // ~12s total loop at speed=1
const TOTAL_SCENES = 5;

// Default speech-bubble scripts (used when captions[] not supplied)
const DEFAULT_BUBBLES = [
  { char: "I want to start a\nbusiness abroad!", partner: null },
  { char: "Can you help me?", partner: "Yes! We're your\nCanadian team 🇨🇦" },
  { char: "It's official — I'm\nincorporated in Canada!", partner: null },
  { char: "Business is growing\nevery month!", partner: null },
  { char: "Now let's go\nglobal 🌍", partner: null },
];

export default function JourneyAnimation({
  enabled = true,
  speed = 1.0,
  captions = [],
  expansionCountries = [],
  fallbackImageUrl = "",
  brandName = "Open Business Anywhere",
}) {
  const prefersReducedMotion = useReducedMotion();
  const wrapperRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const [scene, setScene] = useState(0);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const io = new IntersectionObserver(
      ([e]) => setIsVisible(e.isIntersecting),
      { threshold: 0.15 }
    );
    io.observe(wrapperRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!enabled || prefersReducedMotion || !isVisible) return;
    const clamped = Math.max(0.5, Math.min(2, Number(speed) || 1));
    const t = setInterval(() => setScene((s) => (s + 1) % TOTAL_SCENES), SCENE_DURATION_MS / clamped);
    return () => clearInterval(t);
  }, [enabled, prefersReducedMotion, isVisible, speed]);

  if (!enabled || prefersReducedMotion) {
    return (
      <div ref={wrapperRef} className="relative aspect-[4/5] w-full rounded-sm border border-gos-border overflow-hidden bg-gradient-to-b from-[#0d1b3d] to-[#243a75]">
        {fallbackImageUrl ? (
          <img src={fallbackImageUrl} alt={`${brandName} journey`} className="w-full h-full object-cover opacity-90" loading="lazy" />
        ) : (
          <StaticStage brandName={brandName} />
        )}
      </div>
    );
  }

  const caption = captions[scene] || "";

  return (
    <div
      ref={wrapperRef}
      className="relative aspect-[4/5] w-full rounded-sm border border-gos-border overflow-hidden"
      data-testid="journey-animation"
      aria-label={`${brandName} customer journey animation`}
    >
      {/* Sky gradient background */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #F5EEDC 0%, #E6D9BE 55%, #D6C6A8 100%)" }} />
      {/* Subtle grain */}
      <div className="absolute inset-0 opacity-20 grain pointer-events-none" />

      {/* Scene stage */}
      <svg viewBox="0 0 800 1000" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
        {/* Ground */}
        <rect x="0" y="720" width="800" height="280" fill="#8B6F47" opacity="0.08" />
        <line x1="0" y1="720" x2="800" y2="720" stroke="#8B6F47" strokeWidth="1" strokeDasharray="3 4" opacity="0.35" />

        <AnimatePresence mode="wait">
          {scene === 0 && <SceneAsia key="s0" />}
          {scene === 1 && <SceneCall key="s1" brandName={brandName} />}
          {scene === 2 && <SceneCanada key="s2" />}
          {scene === 3 && <SceneOperate key="s3" />}
          {scene === 4 && <SceneGlobe key="s4" markers={expansionCountries} />}
        </AnimatePresence>
      </svg>

      {/* Bottom caption bar */}
      <div className="absolute inset-x-0 bottom-0 p-5 pb-6" style={{ background: "linear-gradient(180deg, transparent, rgba(13,27,61,0.9))" }}>
        <div className="flex items-center gap-2 mb-2">
          {Array.from({ length: TOTAL_SCENES }).map((_, i) => (
            <span
              key={`scene-dot-${i}`}
              className="h-[2px] flex-1 rounded-full transition-all"
              style={{ background: i === scene ? "#D4A74F" : "rgba(255,255,255,0.22)" }}
            />
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={scene}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
            className="text-white/95 text-sm md:text-base font-medium tracking-wide"
            data-testid="journey-caption"
          >
            {caption}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Top-right subtle scene indicator */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-white/85 backdrop-blur-sm rounded-sm px-2.5 py-1 shadow-sm">
        <span className="text-xs leading-none">🍁</span>
        <span className="text-[9px] uppercase tracking-widest text-gos-navy font-semibold">Journey</span>
      </div>
    </div>
  );
}

/* ═══════════════════════ CHARACTER ═══════════════════════ */
/** Reusable "Sam" character — the entrepreneur. Emotion controls facial expression. */
function Character({ x = 400, y = 620, emotion = "happy", flip = false }) {
  const eyeShape = emotion === "worried" ? { ry: 3, cy: 0 } : { ry: 3.5, cy: 0 };
  const mouths = {
    happy: "M -8 6 Q 0 12 8 6",
    worried: "M -6 8 Q 0 5 6 8",
    excited: "M -9 4 Q 0 14 9 4",
    smile: "M -7 6 Q 0 10 7 6",
    proud: "M -8 5 Q 0 11 8 5",
  };
  const transform = `translate(${x}, ${y}) ${flip ? "scale(-1, 1)" : ""}`;

  return (
    <g transform={transform}>
      {/* Shadow */}
      <ellipse cx="0" cy="140" rx="55" ry="7" fill="#000" opacity="0.15" />

      {/* Legs (navy pants) */}
      <rect x="-22" y="70" width="16" height="70" rx="4" fill="#1a2a52" />
      <rect x="6" y="70" width="16" height="70" rx="4" fill="#1a2a52" />
      {/* Shoes */}
      <ellipse cx="-14" cy="140" rx="12" ry="4" fill="#2b2b2b" />
      <ellipse cx="14" cy="140" rx="12" ry="4" fill="#2b2b2b" />

      {/* Body (white shirt + red tie) */}
      <path d="M -32 0 Q -32 -8 -22 -12 L 22 -12 Q 32 -8 32 0 L 32 70 L -32 70 Z" fill="#F7F3EA" />
      {/* Tie */}
      <path d="M 0 -6 L -6 0 L -3 40 L 3 40 L 6 0 Z" fill="#EF3E42" />
      {/* Suit lapels */}
      <path d="M -32 -6 L -32 60 L -14 20 Z" fill="#1a2a52" />
      <path d="M 32 -6 L 32 60 L 14 20 Z" fill="#1a2a52" />
      {/* Buttons */}
      <circle cx="0" cy="40" r="1.6" fill="#1a2a52" />
      <circle cx="0" cy="55" r="1.6" fill="#1a2a52" />

      {/* Neck */}
      <rect x="-6" y="-20" width="12" height="10" fill="#e0b993" />

      {/* Head */}
      <ellipse cx="0" cy="-40" rx="26" ry="30" fill="#e8c39c" />
      {/* Hair */}
      <path d="M -26 -50 Q -22 -72 0 -70 Q 22 -72 26 -50 Q 24 -60 12 -62 Q 0 -64 -12 -62 Q -24 -60 -26 -50 Z" fill="#3d2817" />
      {/* Eyebrows */}
      <path d="M -14 -46 L -6 -47" stroke="#2a1a0d" strokeWidth="2" strokeLinecap="round" />
      <path d="M 6 -47 L 14 -46" stroke="#2a1a0d" strokeWidth="2" strokeLinecap="round" />
      {/* Eyes */}
      <ellipse cx="-9" cy={-40 + eyeShape.cy} rx="2.2" ry={eyeShape.ry} fill="#2a1a0d" />
      <ellipse cx="9" cy={-40 + eyeShape.cy} rx="2.2" ry={eyeShape.ry} fill="#2a1a0d" />
      {/* Nose */}
      <path d="M 0 -34 L -2 -28 L 2 -28 Z" fill="#c9a075" opacity="0.6" />
      {/* Mouth (dynamic) */}
      <g transform="translate(0, -22)">
        <path d={mouths[emotion] || mouths.happy} fill="none" stroke="#2a1a0d" strokeWidth="2" strokeLinecap="round" />
      </g>
      {/* Ears */}
      <ellipse cx="-26" cy="-38" rx="4" ry="6" fill="#e0b993" />
      <ellipse cx="26" cy="-38" rx="4" ry="6" fill="#e0b993" />
    </g>
  );
}

/* ═══════════════════════ SPEECH BUBBLE ═══════════════════════ */
function SpeechBubble({ x, y, text, color = "white", textColor = "#0d1b3d", tail = "left", delay = 0 }) {
  const lines = String(text).split("\n");
  const width = Math.max(140, Math.max(...lines.map((l) => l.length)) * 8 + 30);
  const height = 30 + lines.length * 20;
  return (
    <motion.g
      transform={`translate(${x}, ${y})`}
      initial={{ opacity: 0, scale: 0.7, y: y + 10 }}
      animate={{ opacity: 1, scale: 1, y }}
      transition={{ delay, duration: 0.5, ease: "backOut" }}
    >
      {/* Shadow */}
      <rect x="4" y="6" width={width} height={height} rx="14" fill="#000" opacity="0.12" />
      {/* Bubble */}
      <rect x="0" y="0" width={width} height={height} rx="14" fill={color} stroke="#1a2a52" strokeWidth="1.5" />
      {/* Tail */}
      {tail === "left" ? (
        <path d={`M 20 ${height} L 8 ${height + 18} L 40 ${height - 2} Z`} fill={color} stroke="#1a2a52" strokeWidth="1.5" strokeLinejoin="round" />
      ) : (
        <path d={`M ${width - 20} ${height} L ${width - 8} ${height + 18} L ${width - 40} ${height - 2} Z`} fill={color} stroke="#1a2a52" strokeWidth="1.5" strokeLinejoin="round" />
      )}
      {/* Text */}
      <text x={width / 2} y="22" textAnchor="middle" fill={textColor} fontSize="15" fontWeight="600" fontFamily="Georgia, serif">
        {lines.map((ln, i) => (
          <tspan key={`${ln}-${i}`} x={width / 2} dy={i === 0 ? 0 : 20}>{ln}</tspan>
        ))}
      </text>
    </motion.g>
  );
}

/* ═══════════════════════ SCENES ═══════════════════════ */
const sceneFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.35 } },
};

/** Scene 1 — Asia: Sam at desk, worried, thought bubble */
function SceneAsia() {
  return (
    <motion.g {...sceneFade}>
      {/* Asian city skyline background */}
      <g opacity="0.4">
        <rect x="30" y="380" width="60" height="340" fill="#8B6F47" />
        <rect x="100" y="320" width="80" height="400" fill="#7a5f3a" />
        <rect x="190" y="410" width="55" height="310" fill="#8B6F47" />
        <rect x="255" y="280" width="90" height="440" fill="#6b5230" />
        {/* Pagoda hint */}
        <path d="M 700 400 L 730 350 L 760 400 Z" fill="#a03b3b" opacity="0.5" />
        <rect x="710" y="400" width="40" height="60" fill="#a03b3b" opacity="0.5" />
        <path d="M 700 460 L 730 425 L 760 460 Z" fill="#a03b3b" opacity="0.5" />
        <rect x="710" y="460" width="40" height="260" fill="#7a2a2a" opacity="0.5" />
        {/* Building windows */}
        {[350, 400, 450, 500, 550, 600].map((y) =>
          [50, 120, 210, 275].map((x) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="6" height="10" fill="#f5c66c" opacity="0.5" />
          ))
        )}
      </g>
      {/* Sun / rising icon */}
      <circle cx="620" cy="180" r="45" fill="#f5c66c" opacity="0.55" />

      {/* Desk */}
      <rect x="280" y="700" width="240" height="12" rx="2" fill="#5a3a1f" />
      <rect x="290" y="712" width="8" height="30" fill="#5a3a1f" />
      <rect x="502" y="712" width="8" height="30" fill="#5a3a1f" />
      {/* Laptop on desk */}
      <g transform="translate(400, 690)">
        <rect x="-30" y="-2" width="60" height="4" rx="1" fill="#333" />
        <rect x="-28" y="-30" width="56" height="30" fill="#0d1b3d" stroke="#333" strokeWidth="1.2" />
        <rect x="-24" y="-27" width="48" height="24" fill="#4CD9E4" />
      </g>

      {/* Character behind desk */}
      <Character x={400} y={620} emotion="worried" />

      {/* Thought bubble */}
      <SpeechBubble x={470} y={340} text={"I want to start a\nbusiness abroad!"} delay={0.4} tail="left" />
      {/* Thought dots trailing to head */}
      <circle cx="455" cy="440" r="4" fill="white" stroke="#1a2a52" strokeWidth="1" />
      <circle cx="440" cy="470" r="3" fill="white" stroke="#1a2a52" strokeWidth="1" />

      {/* Location pin badge */}
      <g transform="translate(60, 60)">
        <rect x="0" y="0" width="90" height="26" rx="13" fill="#1a2a52" />
        <text x="45" y="18" textAnchor="middle" fill="#D4A74F" fontSize="11" fontWeight="700" letterSpacing="2">ASIA</text>
      </g>
    </motion.g>
  );
}

/** Scene 2 — Video call to Canadian team member */
function SceneCall({ brandName }) {
  return (
    <motion.g {...sceneFade}>
      {/* Sam on the left */}
      <Character x={220} y={620} emotion="smile" />
      <SpeechBubble x={40} y={340} text={"Can you help me?"} delay={0.3} tail="right" />

      {/* Video call arc */}
      <motion.path
        d="M 340 500 Q 400 380 460 500"
        fill="none" stroke="#D4A74F" strokeWidth="2.5" strokeDasharray="6 6" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, delay: 0.4 }}
      />
      {/* Center logo pill */}
      <motion.g
        transform="translate(340, 380)"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.9, duration: 0.5 }}
      >
        <rect x="0" y="0" width="120" height="34" rx="17" fill="white" stroke="#1a2a52" strokeWidth="1.5" />
        <text x="60" y="16" textAnchor="middle" fill="#EF3E42" fontSize="14">🍁</text>
        <text x="60" y="30" textAnchor="middle" fill="#0d1b3d" fontSize="8" fontWeight="700" letterSpacing="0.5">
          {String(brandName).toUpperCase().slice(0, 24)}
        </text>
      </motion.g>

      {/* Canadian agent on the right */}
      <CanadianAgent x={580} y={620} />
      <SpeechBubble x={545} y={340} text={"Yes! We're your\nCanadian team 🇨🇦"} color="#EF3E42" textColor="#fff" delay={1.0} tail="left" />
    </motion.g>
  );
}

/** Simple variant character = the Canadian agent (different hair + suit) */
function CanadianAgent({ x, y }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <ellipse cx="0" cy="140" rx="55" ry="7" fill="#000" opacity="0.15" />
      {/* Legs */}
      <rect x="-22" y="70" width="16" height="70" rx="4" fill="#2b2b2b" />
      <rect x="6" y="70" width="16" height="70" rx="4" fill="#2b2b2b" />
      <ellipse cx="-14" cy="140" rx="12" ry="4" fill="#111" />
      <ellipse cx="14" cy="140" rx="12" ry="4" fill="#111" />
      {/* Red blazer */}
      <path d="M -32 0 Q -32 -8 -22 -12 L 22 -12 Q 32 -8 32 0 L 32 70 L -32 70 Z" fill="#EF3E42" />
      <path d="M -32 -6 L -32 60 L -14 20 Z" fill="#B02328" />
      <path d="M 32 -6 L 32 60 L 14 20 Z" fill="#B02328" />
      {/* White blouse */}
      <path d="M -8 -8 L 0 6 L 8 -8 L 8 30 L -8 30 Z" fill="#F7F3EA" />
      {/* Maple pin */}
      <text x="-16" y="8" fontSize="10">🍁</text>
      {/* Neck */}
      <rect x="-6" y="-20" width="12" height="10" fill="#f0d4b0" />
      {/* Head */}
      <ellipse cx="0" cy="-40" rx="26" ry="30" fill="#f4dcbb" />
      {/* Blonde hair (longer) */}
      <path d="M -26 -50 Q -30 -80 0 -72 Q 30 -80 26 -50 L 28 -20 L 22 -30 Q 0 -66 -22 -30 L -28 -20 Z" fill="#c9962f" />
      {/* Eyes */}
      <ellipse cx="-9" cy="-40" rx="2.2" ry="3.5" fill="#2a1a0d" />
      <ellipse cx="9" cy="-40" rx="2.2" ry="3.5" fill="#2a1a0d" />
      {/* Eyebrows */}
      <path d="M -14 -46 L -6 -47" stroke="#8a6a2a" strokeWidth="2" strokeLinecap="round" />
      <path d="M 6 -47 L 14 -46" stroke="#8a6a2a" strokeWidth="2" strokeLinecap="round" />
      {/* Big smile */}
      <path d="M -8 -18 Q 0 -10 8 -18" fill="none" stroke="#2a1a0d" strokeWidth="2" strokeLinecap="round" />
    </g>
  );
}

/** Scene 3 — Canada setup, Sam proudly holds certificate */
function SceneCanada() {
  return (
    <motion.g {...sceneFade}>
      {/* Canadian sky + Rockies */}
      <path d="M 0 500 L 120 380 L 220 480 L 340 340 L 480 470 L 600 360 L 720 470 L 800 420 L 800 720 L 0 720 Z" fill="#8ea9c9" opacity="0.3" />
      {/* Sun */}
      <circle cx="150" cy="180" r="42" fill="#f5c66c" opacity="0.55" />

      {/* CN Tower */}
      <g transform="translate(600, 720)">
        <rect x="-3" y="-320" width="6" height="320" fill="#5a5a5a" />
        <ellipse cx="0" cy="-260" rx="18" ry="10" fill="#D4A74F" />
        <rect x="-14" y="-260" width="28" height="20" fill="#3a3a3a" />
        <rect x="-1" y="-360" width="2" height="40" fill="#5a5a5a" />
      </g>

      {/* Building */}
      <g transform="translate(110, 720)">
        <rect x="-70" y="-200" width="140" height="200" fill="#f5f0e2" stroke="#8B6F47" strokeWidth="1.5" />
        {/* Big maple flag on top */}
        <rect x="0" y="-260" width="2" height="60" fill="#333" />
        <rect x="2" y="-260" width="35" height="22" fill="#EF3E42" />
        <text x="19" y="-244" textAnchor="middle" fontSize="16">🍁</text>
        {/* Windows */}
        {[-160, -130, -100, -70, -40].map((y) =>
          [-45, -15, 15, 45].map((x) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="18" height="18" fill="#4CD9E4" opacity="0.7" stroke="#5a5a5a" strokeWidth="0.5" />
          ))
        )}
        {/* Door */}
        <rect x="-14" y="-40" width="28" height="40" fill="#5a3a1f" />
      </g>

      {/* Sam holding certificate */}
      <Character x={400} y={620} emotion="proud" />

      {/* Certificate — big scroll in hand */}
      <motion.g
        transform="translate(400, 540)"
        initial={{ scale: 0, rotate: -15 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.4, duration: 0.5, ease: "backOut" }}
      >
        <rect x="-45" y="-30" width="90" height="60" fill="#faf6ea" stroke="#D4A74F" strokeWidth="2" />
        <text x="0" y="-16" textAnchor="middle" fontSize="6" fill="#0d1b3d" fontWeight="700" letterSpacing="1">CERTIFICATE</text>
        <text x="0" y="-8" textAnchor="middle" fontSize="4" fill="#666">OF INCORPORATION</text>
        <line x1="-30" y1="0" x2="30" y2="0" stroke="#999" strokeWidth="0.5" />
        <line x1="-30" y1="6" x2="30" y2="6" stroke="#999" strokeWidth="0.5" />
        <line x1="-30" y1="12" x2="15" y2="12" stroke="#999" strokeWidth="0.5" />
        {/* Red seal */}
        <circle cx="25" cy="22" r="6" fill="#EF3E42" />
        <text x="25" y="24" textAnchor="middle" fontSize="4" fill="#fff">🍁</text>
      </motion.g>

      {/* Speech bubble */}
      <SpeechBubble x={430} y={280} text={"It's official — I'm\nincorporated in Canada!"} delay={0.8} tail="left" />

      {/* Confetti */}
      {[
        [200, 200, "#EF3E42"], [280, 160, "#D4A74F"], [520, 180, "#4CD9E4"],
        [620, 220, "#EF3E42"], [340, 240, "#D4A74F"], [560, 260, "#4CD9E4"],
      ].map(([cx, cy, col], i) => (
        <motion.rect
          key={`confetti-${cx}-${cy}-${col}`}
          x={cx} y={cy} width="8" height="4" fill={col}
          initial={{ y: cy - 30, opacity: 0, rotate: 0 }}
          animate={{ y: cy + 200, opacity: [0, 1, 1, 0], rotate: 360 }}
          transition={{ duration: 2.2, delay: 0.6 + i * 0.1, repeat: Infinity, repeatDelay: 0.5 }}
        />
      ))}

      {/* Canada tag */}
      <g transform="translate(60, 60)">
        <rect x="0" y="0" width="120" height="26" rx="13" fill="#EF3E42" />
        <text x="60" y="18" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700" letterSpacing="2">CANADA 🍁</text>
      </g>
    </motion.g>
  );
}

/** Scene 4 — Business active. Sam at desk with growth chart */
function SceneOperate() {
  return (
    <motion.g {...sceneFade}>
      {/* Office wall */}
      <rect x="0" y="0" width="800" height="720" fill="#faf6ea" opacity="0.3" />
      {/* Growth chart poster */}
      <g transform="translate(140, 200)">
        <rect x="-80" y="-80" width="220" height="160" fill="#fff" stroke="#0d1b3d" strokeWidth="2" />
        <text x="30" y="-60" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0d1b3d">GROWTH</text>
        {/* Bars */}
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.rect
            key={`growth-bar-${i}`}
            x={-60 + i * 30} y={40 - i * 12} width="20" height={20 + i * 12}
            fill={i === 4 ? "#22c55e" : "#D4A74F"}
            initial={{ height: 0, y: 40 }}
            animate={{ height: 20 + i * 12, y: 40 - i * 12 }}
            transition={{ delay: 0.3 + i * 0.15, duration: 0.4 }}
          />
        ))}
        {/* Arrow up */}
        <motion.path
          d="M -60 45 L 60 -20 L 55 -10 M 60 -20 L 48 -22"
          fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 1.0, duration: 0.8 }}
        />
      </g>

      {/* Icons floating around Sam */}
      {[
        { icon: "💰", x: 620, y: 200, delay: 0.2 },
        { icon: "📦", x: 680, y: 320, delay: 0.4 },
        { icon: "📊", x: 640, y: 440, delay: 0.6 },
        { icon: "🤝", x: 130, y: 480, delay: 0.8 },
        { icon: "🌐", x: 590, y: 540, delay: 1.0 },
      ].map((f) => (
        <motion.g
          key={`float-${f.icon}`}
          initial={{ opacity: 0, scale: 0, y: f.y + 20 }}
          animate={{ opacity: 1, scale: 1, y: [f.y, f.y - 10, f.y] }}
          transition={{
            opacity: { delay: f.delay, duration: 0.4 },
            scale: { delay: f.delay, duration: 0.4, ease: "backOut" },
            y: { delay: f.delay, duration: 2.5, repeat: Infinity, ease: "easeInOut" },
          }}
        >
          <circle cx={f.x} cy={f.y} r="26" fill="white" stroke="#D4A74F" strokeWidth="2" />
          <text x={f.x} y={f.y + 10} textAnchor="middle" fontSize="26">{f.icon}</text>
        </motion.g>
      ))}

      {/* Desk */}
      <rect x="280" y="700" width="240" height="12" rx="2" fill="#5a3a1f" />
      <rect x="290" y="712" width="8" height="30" fill="#5a3a1f" />
      <rect x="502" y="712" width="8" height="30" fill="#5a3a1f" />
      {/* Coffee mug */}
      <g transform="translate(320, 682)">
        <rect x="-8" y="-12" width="16" height="18" rx="2" fill="#EF3E42" />
        <path d="M 8 -8 Q 14 -8 14 -4 Q 14 0 8 0" fill="none" stroke="#EF3E42" strokeWidth="2" />
        <ellipse cx="0" cy="-12" rx="8" ry="2" fill="#3d2817" />
      </g>
      {/* Laptop */}
      <g transform="translate(400, 690)">
        <rect x="-30" y="-2" width="60" height="4" rx="1" fill="#333" />
        <rect x="-28" y="-30" width="56" height="30" fill="#0d1b3d" stroke="#333" strokeWidth="1.2" />
        {/* Green line = growth on screen */}
        <path d="M -20 -8 L -8 -12 L 4 -18 L 16 -22 L 22 -25" fill="none" stroke="#22c55e" strokeWidth="1.5" />
      </g>

      <Character x={400} y={620} emotion="excited" />
      <SpeechBubble x={430} y={310} text={"Business is growing\nevery month!"} delay={1.2} tail="left" />
    </motion.g>
  );
}

/** Scene 5 — Globe expansion */
function SceneGlobe({ markers = [] }) {
  const CX = 400, CY = 440, R = 150;
  // Positions of markers around the globe (angle placement)
  const angles = [-140, -100, -60, -20, 20, 60, 100, 140]; // degrees around globe
  const marks = markers.slice(0, 8).map((name, i) => {
    const rad = (angles[i] * Math.PI) / 180;
    return {
      name,
      x: CX + Math.cos(rad) * (R + 40),
      y: CY + Math.sin(rad) * (R + 40),
    };
  });

  return (
    <motion.g {...sceneFade}>
      {/* Globe */}
      <motion.g
        style={{ transformOrigin: `${CX}px ${CY}px` }}
        animate={{ rotate: 360 }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      >
        <circle cx={CX} cy={CY} r={R} fill="#4CD9E4" opacity="0.9" />
        {/* Continents */}
        <path d={`M ${CX - 80} ${CY - 40} Q ${CX - 60} ${CY - 80} ${CX - 20} ${CY - 60} L ${CX - 10} ${CY - 20} L ${CX - 60} ${CY} Z`} fill="#22c55e" opacity="0.85" />
        <path d={`M ${CX - 40} ${CY + 20} Q ${CX - 20} ${CY + 60} ${CX + 10} ${CY + 80} L ${CX - 10} ${CY + 100} L ${CX - 50} ${CY + 60} Z`} fill="#22c55e" opacity="0.85" />
        <path d={`M ${CX + 20} ${CY - 60} Q ${CX + 80} ${CY - 40} ${CX + 90} ${CY - 10} L ${CX + 60} ${CY + 30} L ${CX + 30} ${CY} Z`} fill="#22c55e" opacity="0.85" />
        <path d={`M ${CX + 40} ${CY + 40} Q ${CX + 90} ${CY + 30} ${CX + 100} ${CY + 60} L ${CX + 60} ${CY + 80} Z`} fill="#22c55e" opacity="0.85" />
        {/* Meridians */}
        <ellipse cx={CX} cy={CY} rx={R} ry={R * 0.35} fill="none" stroke="#fff" strokeWidth="1" opacity="0.5" />
        <ellipse cx={CX} cy={CY} rx={R * 0.35} ry={R} fill="none" stroke="#fff" strokeWidth="1" opacity="0.5" />
      </motion.g>
      {/* Globe outline (static, does not rotate) */}
      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#0d1b3d" strokeWidth="3" />

      {/* Country markers around the globe */}
      {marks.map((m, i) => (
        <motion.g
          key={m.name}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 + i * 0.15, duration: 0.4, ease: "backOut" }}
        >
          {/* Connector */}
          <line x1={CX} y1={CY} x2={m.x} y2={m.y} stroke="#D4A74F" strokeWidth="1.2" strokeDasharray="4 3" opacity="0.6" />
          {/* Pin */}
          <circle cx={m.x} cy={m.y} r="14" fill="#EF3E42" />
          <circle cx={m.x} cy={m.y} r="6" fill="#fff" />
          {/* Label */}
          <rect x={m.x - 40} y={m.y + 20} width="80" height="18" rx="9" fill="white" stroke="#0d1b3d" strokeWidth="1" />
          <text x={m.x} y={m.y + 32} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0d1b3d">{m.name}</text>
        </motion.g>
      ))}

      {/* Sam pointing to globe */}
      <g transform="translate(120, 0)">
        <Character x={100} y={620} emotion="excited" />
      </g>
      <SpeechBubble x={40} y={280} text={"Now let's go\nglobal 🌍"} delay={1.4} tail="left" />
    </motion.g>
  );
}

/* ═══════════════════════ STATIC FALLBACK ═══════════════════════ */
function StaticStage({ brandName }) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-[#F5EEDC] to-[#D6C6A8]">
      <div className="text-center px-6">
        <div className="text-6xl mb-4">🍁</div>
        <div className="text-gos-navy font-display text-2xl">Start Anywhere.</div>
        <div className="text-gos-sand font-display text-2xl italic">Expand Everywhere.</div>
        <div className="mt-4 text-xs text-gos-muted uppercase tracking-widest">{brandName}</div>
      </div>
    </div>
  );
}

/* ═══════════════════════ MOBILE VARIANT ═══════════════════════ */
export function JourneyAnimationMobile({ enabled = true, captions = [], brandName = "Open Business Anywhere" }) {
  const prefersReducedMotion = useReducedMotion();
  const wrapperRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const io = new IntersectionObserver(([e]) => setIsVisible(e.isIntersecting), { threshold: 0.15 });
    io.observe(wrapperRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!enabled || prefersReducedMotion || !isVisible) return;
    const t = setInterval(() => setStep((s) => (s + 1) % 4), 2200);
    return () => clearInterval(t);
  }, [enabled, prefersReducedMotion, isVisible]);

  const steps = [
    { emoji: "🤔", label: "Asia Entrepreneur", caption: captions[0] || "I want to start a business!" },
    { emoji: "🍁", label: brandName, caption: captions[1] || "We're here to help!" },
    { emoji: "🏢", label: "Canada Business Setup", caption: captions[2] || "Officially incorporated!" },
    { emoji: "🌍", label: "Global Expansion", caption: captions[4] || "Now scaling worldwide!" },
  ];

  if (!enabled || prefersReducedMotion) {
    return (
      <div ref={wrapperRef} className="relative aspect-[4/5] w-full rounded-sm overflow-hidden bg-gradient-to-b from-[#F5EEDC] to-[#D6C6A8]">
        <StaticStage brandName={brandName} />
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative aspect-[4/5] w-full rounded-sm border border-gos-border overflow-hidden p-5 flex flex-col justify-center gap-3 bg-gradient-to-b from-[#F5EEDC] to-[#D6C6A8]"
      data-testid="journey-animation-mobile"
    >
      <div className="grain absolute inset-0 opacity-20 pointer-events-none" />
      {steps.map((s, i) => (
        <motion.div
          key={s.label}
          animate={{
            opacity: step === i ? 1 : 0.4,
            scale: step === i ? 1 : 0.96,
            borderColor: step === i ? "#D4A74F" : "rgba(26,42,82,0.15)",
            backgroundColor: step === i ? "#fff" : "rgba(255,255,255,0.55)",
          }}
          transition={{ duration: 0.45 }}
          className="border-2 rounded-sm p-3 flex items-center gap-3 relative shadow-sm"
        >
          <div className="text-3xl">{s.emoji}</div>
          <div className="flex-1">
            <div className="text-gos-navy text-sm font-semibold">{s.label}</div>
            {step === i && s.caption && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11px] text-gos-slate mt-0.5 italic">
                &ldquo;{s.caption}&rdquo;
              </motion.div>
            )}
          </div>
          {step === i && <span className="w-2 h-2 rounded-full bg-gos-maple" />}
        </motion.div>
      ))}
    </div>
  );
}
